import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import DynamicBreadcrumbs from "../../components/Breadcrumbs/DynamicBreadcrumbs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  Users,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
  Activity,
  Clock,
  Lock,
  Unlock,
  AlertTriangle,
} from "lucide-react";

const ActiveDirectoryResultsPage = () => {
  const { id } = useParams();

  // Mock Data for 9 Stats
  const stats = [
    {
      label: "Total Users",
      value: "15,420",
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      label: "Service Accounts",
      value: "450",
      icon: UserCheck,
      color: "text-indigo-600",
      bg: "bg-indigo-100",
    },
    {
      label: "Privileged Accounts",
      value: "85",
      icon: ShieldAlert,
      color: "text-red-600",
      bg: "bg-red-100",
    },
    {
      label: "Protected Users",
      value: "120",
      icon: ShieldCheck,
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      label: "Active Sessions",
      value: "3,240",
      icon: Activity,
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
    {
      label: "Failed Logins (24h)",
      value: "142",
      icon: AlertTriangle,
      color: "text-orange-600",
      bg: "bg-orange-100",
    },
    {
      label: "Locked Accounts",
      value: "12",
      icon: Lock,
      color: "text-gray-600",
      bg: "bg-gray-100",
    },
    {
      label: "Password Expires Soon",
      value: "350",
      icon: Clock,
      color: "text-yellow-600",
      bg: "bg-yellow-100",
    },
    {
      label: "Stale Accounts",
      value: "56",
      icon: Unlock,
      color: "text-pink-600",
      bg: "bg-pink-100",
    },
  ];

  // Mock Data for Login History
  const loginHistory = [
    {
      id: 1,
      user: "jdoe@ad.acaws.stackguard.io",
      ip: "192.168.1.105",
      type: "Interactive",
      status: "Success",
      time: "2024-05-20 10:15:22",
      location: "New York, US",
    },
    {
      id: 2,
      user: "admin@ad.acaws.stackguard.io",
      ip: "10.0.0.5",
      type: "Service Logon",
      status: "Success",
      time: "2024-05-20 10:12:05",
      location: "Internal",
    },
    {
      id: 3,
      user: "mgupta@ad.acaws.stackguard.io",
      ip: "192.168.1.112",
      type: "Network",
      status: "Failed",
      time: "2024-05-20 10:05:41",
      location: "London, UK",
    },
    {
      id: 4,
      user: "svc_backup@ad.acaws.stackguard.io",
      ip: "10.0.1.20",
      type: "Batch",
      status: "Success",
      time: "2024-05-20 09:55:10",
      location: "Internal",
    },
    {
      id: 5,
      user: "asmith@ad.acaws.stackguard.io",
      ip: "192.168.1.101",
      type: "Interactive",
      status: "Success",
      time: "2024-05-20 09:42:33",
      location: "San Francisco, US",
    },
    {
      id: 6,
      user: "guest@ad.acaws.stackguard.io",
      ip: "192.168.1.200",
      type: "Network",
      status: "Failed",
      time: "2024-05-20 09:30:15",
      location: "Unknown",
    },
    {
      id: 7,
      user: "dba_user@ad.acaws.stackguard.io",
      ip: "10.0.2.50",
      type: "Service Logon",
      status: "Success",
      time: "2024-05-20 09:15:00",
      location: "Internal",
    },
    {
      id: 8,
      user: "klee@ad.acaws.stackguard.io",
      ip: "192.168.1.108",
      type: "Interactive",
      status: "Success",
      time: "2024-05-20 08:55:45",
      location: "Seoul, KR",
    },
  ];

  // Mock Data for Statistics Graph (Logins per hour)
  const graphData = [
    { hour: "00:00", logins: 120 },
    { hour: "01:00", logins: 85 },
    { hour: "02:00", logins: 50 },
    { hour: "03:00", logins: 45 },
    { hour: "04:00", logins: 60 },
    { hour: "05:00", logins: 150 },
    { hour: "06:00", logins: 380 },
    { hour: "07:00", logins: 850 },
    { hour: "08:00", logins: 1200 },
    { hour: "09:00", logins: 1540 },
    { hour: "10:00", logins: 1680 },
    { hour: "11:00", logins: 1450 },
    { hour: "12:00", logins: 1100 },
    { hour: "13:00", logins: 1350 },
    { hour: "14:00", logins: 1600 },
    { hour: "15:00", logins: 1550 },
    { hour: "16:00", logins: 1400 },
    { hour: "17:00", logins: 1150 },
    { hour: "18:00", logins: 750 },
    { hour: "19:00", logins: 500 },
    { hour: "20:00", logins: 420 },
    { hour: "21:00", logins: 350 },
    { hour: "22:00", logins: 280 },
    { hour: "23:00", logins: 190 },
  ];

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-6 space-y-2">
          <DynamicBreadcrumbs />
          <h1 className="text-black text-2xl md:text-3xl font-bold">
            Active Directory Results: {id}
          </h1>
          <p className="text-gray-600 mt-2">
            Detailed security analysis and activity monitoring for {id} domain.
          </p>
        </div>

        {/* 9 Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          {stats.map((stat, index) => (
            <Card key={index} className="border border-gray-200 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="mb-4 bg-gray-100 p-1 rounded-lg w-fit">
            <TabsTrigger
              value="activity"
              className="px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#441a9a] data-[state=active]:shadow-sm rounded-md transition-all"
            >
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger
              value="statistics"
              className="px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#441a9a] data-[state=active]:shadow-sm rounded-md transition-all"
            >
              <Users className="w-4 h-4 mr-2" />
              Statistics
            </TabsTrigger>
          </TabsList>

          {/* Activity Tab Content */}
          <TabsContent value="activity">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-4">
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Login History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="w-[25%] font-semibold">
                        User
                      </TableHead>
                      <TableHead className="w-[15%] font-semibold">
                        IP Address
                      </TableHead>
                      <TableHead className="w-[15%] font-semibold">
                        Location
                      </TableHead>
                      <TableHead className="w-[15%] font-semibold">
                        Logon Type
                      </TableHead>
                      <TableHead className="w-[15%] font-semibold">
                        Time
                      </TableHead>
                      <TableHead className="w-[15%] font-semibold text-right">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loginHistory.map((item) => (
                      <TableRow
                        key={item.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell className="font-medium text-gray-900">
                          {item.user}
                        </TableCell>
                        <TableCell className="text-gray-600 font-mono text-sm">
                          {item.ip}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.location}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            {item.type}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap">
                          {item.time}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              item.status === "Success"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}
                          >
                            {item.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab Content */}
          <TabsContent value="statistics">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-4">
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Authentication Activity (24h)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={graphData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 10,
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="colorLogins"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#441a9a"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#441a9a"
                            stopOpacity={0.05}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#E5E7EB"
                      />
                      <XAxis
                        dataKey="hour"
                        stroke="#6B7280"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        stroke="#6B7280"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                        dx={-10}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #E5E7EB",
                          borderRadius: "0.5rem",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                        itemStyle={{ color: "#441a9a", fontWeight: "bold" }}
                        formatter={(value) => [`${value} Logins`, "Activity"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="logins"
                        stroke="#441a9a"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorLogins)"
                        activeDot={{ r: 6, strokeWidth: 0, fill: "#441a9a" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ActiveDirectoryResultsPage;

